import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { useDescriptionTemplateStore } from '../../stores/descriptionTemplateStore';
import { DescriptionTemplateEditor } from '../templates/description/DescriptionTemplateEditor';
import type { Product } from '../../types/product';

interface DescriptionInputProps {
  description: string;
  condition: string;
  product: Product;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onTemplateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  useTemplate: boolean;
}

export function DescriptionInput({
  description,
  condition,
  product,
  onChange,
  onTemplateChange,
  useTemplate
}: DescriptionInputProps) {
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [localDescription, setLocalDescription] = useState(description);
  const { 
    templates, 
    activeTemplateId, 
    setActiveTemplate, 
    getTemplateByCondition 
  } = useDescriptionTemplateStore();

  // Update local description when prop changes
  useEffect(() => {
    setLocalDescription(description);
    setIsDirty(false);
  }, [description]);

  // Update description when condition changes and template is in use
  useEffect(() => {
    if (useTemplate && condition) {
      // If product has a saved template ID, use that template
      const templateId = product.descriptionTemplateId || activeTemplateId;
      const template = templateId ? 
        templates.find(t => t.id === templateId)?.contents[condition as keyof typeof templates[0]['contents']] :
        getTemplateByCondition(condition);

      if (template) {
        setActiveTemplate(templateId);
        setLocalDescription(template);
        onChange({
          target: { name: 'description', value: template }
        } as React.ChangeEvent<HTMLTextAreaElement>);

        // Update template ID in product if it's different
        if (templateId !== product.descriptionTemplateId) {
          onChange({
            target: { name: 'descriptionTemplateId', value: templateId }
          } as React.ChangeEvent<HTMLInputElement>);
        }
      }
      else{
        setActiveTemplate(null);
      }
    }
  }, [useTemplate, condition, activeTemplateId, product.descriptionTemplateId]);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalDescription(e.target.value);
    setIsDirty(true);
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value || null;

    setActiveTemplate(templateId);
    
    // Save template ID to product
    onChange({
      target: { name: 'descriptionTemplateId', value: templateId }
    } as React.ChangeEvent<HTMLInputElement>);

  };

  useEffect(() => {
    const template = templates.find(t => t.id === activeTemplateId)?.contents[condition as keyof typeof templates[0]['contents']];
    setLocalDescription(template ?? '');
    onChange({
      target: { name: 'description', value: template }
    } as React.ChangeEvent<HTMLTextAreaElement>);
  }, [activeTemplateId]);

  const handleSave = () => {
    if (isDirty) {
      onChange({
        target: { name: 'description', value: localDescription }
      } as React.ChangeEvent<HTMLTextAreaElement>);
      setIsDirty(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-800">商品説明</h4>
          <button
        onClick={() => setShowTemplateEditor(true)}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
        aria-label="テンプレートを編集"
          >
        <Settings className="w-4 h-4" />
        <span>編集</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          
          <div className="flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              id='use-description-template'
              checked={useTemplate}
              onChange={onTemplateChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor='use-description-template' className="ml-2 text-sm text-gray-700 cursor-pointer">状態に合わせたテンプレートを使用</label>
          </div>

          {useTemplate && <select
            value={product.descriptionTemplateId || ''}
            onChange={handleTemplateChange}
            className="w-48 rounded p-2 border border-gray-300 text-sm bg-white focus:ring-blue-500 focus:border-blue-500"
            disabled={!useTemplate}
          >
            <option value="">テンプレートを選択...</option>
            {templates
              .filter(template => template.contents[condition as keyof typeof template.contents])
              .map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
          </select>}
          
        </div>
      </div>
      <textarea
        name="description"
        value={localDescription}
        onChange={handleDescriptionChange}
        rows={12}
        className="mt-1 block w-full rounded-md border p-2 border-gray-300 bg-gray-50 font-mono text-sm"
        placeholder="商品の説明を入力してください"
        disabled={useTemplate && condition !== '' && !product.allowHtmlInDescription}
      />

      {isDirty && (
        <div className="flex justify-end mt-4">
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            保存
          </button>
        </div>
      )}

      {showTemplateEditor && (
        <DescriptionTemplateEditor
          onClose={() => setShowTemplateEditor(false)}
        />
      )}
    </div>
  );
}